import { AcceptPage, PolicyRaw } from '@/cms/definitions/content-types';
import store from '@/store/store';
import { CmsField, CmsPolicy, DigitalAcceptCmsPage, RadioButtonField } from '@/types/types';
import { cloneDeep } from 'lodash';
import { getBrand, getQueryParameter, isDevelop } from './environmentService';
import { formatDate, formatter } from './FormatService';
import { getCancellationDate, getReplacedValue } from './FieldService';
import { POLICY_FIELDS } from '@/types/enums';
import { getProposal } from './apiService';
import { Log } from '@/appinsights/logging/log';

export const setupReplacements = (
	cmsModel: AcceptPage,
	proposal: DigitalAcceptCmsPage,
	replacements: Map<string, string>
) => {
	cmsModel.replacements?.forEach((replacement: { key: string; value: string }) => {
		replacements.set(`{{${replacement.key}}}`, replacement.value);
	});
	// make certain brand is set
	if (!replacements.get('{{SYSTEM_BRAND}}')) {
		replacements.set('{{SYSTEM_BRAND}}', getBrand());
	}
	replacements.set('{{SYSTEM_OFFER_ID}}', proposal.quote_id ?? 'Ukendt tilbud');
	replacements.set('{{SYSTEM_TOTAL_PRICE}}', `${formatter.format(proposal.price)}`);
	if (proposal.fees) {
		replacements.set('{{SYSTEM_TOTAL_FEES}}', `${formatter.format(proposal.fees)}`);
	}

	const currentProductTypesArray: string[] = [];
	const currentProductTitlesArray: string[] = [];

	proposal?.policies?.forEach((policy) => {
		if (!currentProductTypesArray.includes(policy.type)) {
			currentProductTypesArray.push(policy.type);
			if (cmsModel.contentType === 'AcceptPage') {
				const title = getCmsPolicy(cmsModel, policy.type)?.content?.title;
				title ? currentProductTitlesArray.push(title) : currentProductTitlesArray.push(policy.type);
			} else {
				cmsModel.productNames.forEach((prod) => {
					if (prod.key.toLowerCase() === policy.type.toLowerCase()) {
						prod.value
							? currentProductTitlesArray.push(prod.value)
							: currentProductTitlesArray.push(policy.type);
					}
				});
			}
		}
	});

	replacements.set(
		'{{SYSTEM_CURRENT_PRODUCTS}}',
		currentProductTitlesArray.join(', ').replace(/,(?=[^,]*$)/, ' og') // replace last , with ' og'
	);
};

export const handleTermination = (stepId: string, inputFields: CmsField[], policy) => {
	const coverStart = getCancellationDate(stepId, inputFields);
	if (coverStart) {
		const cancellationOptions = inputFields.find(
			(field) => field.id === POLICY_FIELDS.termination_option
		) as RadioButtonField;

		if (cancellationOptions) {
			cancellationOptions.labels[0] += ` - ${formatDate(coverStart)}`;
		}
	}

	const cancelExistingPolicy = inputFields.find((field) => field.id === POLICY_FIELDS.cancelExistingPolicy);

	if (!cancelExistingPolicy) {
		return;
	}

	if (!cancelExistingPolicy?.value || cancelExistingPolicy?.value === 'false') {
		cancelExistingPolicy.value = 'false';
		cancelExistingPolicy.hidden = true;
		cancelExistingPolicy.isValid = true;
	}
	// set termination to true if there is a company
	if (policy?.termination?.[POLICY_FIELDS.previous_company_id]) {
		if (cancelExistingPolicy) {
			cancelExistingPolicy.value = 'true';
			cancelExistingPolicy.readonly = true;
			cancelExistingPolicy.isValid = true;
			cancelExistingPolicy.hidden = false;
		}
	}
	if (cancelExistingPolicy.value === 'true') {
		const cancelExistingPolicyText = inputFields.find(
			(field) => field.id === POLICY_FIELDS.cancelExistingPolicyText
		);
		if (cancelExistingPolicyText) {
			cancelExistingPolicyText.readonly = true;
		}
	}
};

export const handlePayment = (inputFields: CmsField[]) => {
	const paymentMethod = inputFields?.find((field) => field.id === POLICY_FIELDS.payment_method);
	if (paymentMethod?.value === 'PBS') {
		paymentMethod.readonly = true;
		// hide regNo and account
		const regnNoAccount = inputFields?.filter(
			(field) => field.id === POLICY_FIELDS.account || field.id === POLICY_FIELDS.reg_no
		);
		regnNoAccount?.forEach((field) => {
			field.hidden = true;
			field.isValid = true;
			field.dependentId = undefined;
			field.dependentValue = undefined;
		});
	}
};

export const getCmsPolicy = (cmsModel: AcceptPage, policyType: string): PolicyRaw => {
	return cmsModel.adjustPoliciesBlock?.policyBlock?.items?.find((policyBlock) => {
		if (policyBlock.content.type.toLocaleLowerCase() === policyType.toLocaleLowerCase()) {
			return policyBlock;
		}
	});
};

export const getModifiedCmsPolicy = (cmsModel: AcceptPage, policyType: string): CmsPolicy => {
	const cmsPolicy = getCmsPolicy(cmsModel, policyType);
	// always work on copy of content structure
	const policy = cloneDeep(cmsPolicy.content);
	simplifyInputFields(policy);
	return policy as any;
};

// remove api layers
export const simplifyInputFields = (step) => {
	const inputFields = [];
	// when develop input fields are already mapped when auto compiling
	if (step.inputFields?.items) {
		step.inputFields?.items?.forEach((field) => {
			inputFields.push(field.content);
		});
		step.inputFields = inputFields || [];
	}
};

export const resolveLoadingError = (errorCode?: string): string => {
	store.state.hasError = true;
	const errorMsg = store.state.errorMessages.get(errorCode) ?? store.state.errorMessages.get('default');
	if (isDevelop) {
		return errorMsg + ` <br><br>(error code: ${errorCode})`;
	}
	return errorMsg;
};

export const resolveModalError = (errorCode?: string, title?: string): void => {
	let errorMsg = store.state.errorMessages.get(errorCode) ?? store.state.errorMessages.get('default');
	if (isDevelop) {
		errorMsg += ` <br><br>(error code: ${errorCode})`;
	}

	// setup fail modal as default
	const modal: any = {};
	modal.show = true;
	modal.error = true;
	modal.id = 'errorModal';
	modal.track = false; // true?
	modal.trackToken = undefined;
	modal.content = errorMsg;
	modal.btnStyle = 'Button Button__tertiary';
	modal.btnLabel = 'Ok';
	modal.btnIcon = undefined;
	modal.btnSecondStyle = undefined;
	modal.btnSecondLabel = undefined;
	modal.btnInlineLabel = undefined;
	modal.btnInlineAction = undefined;
	modal.btnInlinestyle = undefined;
	modal.title = title ?? 'Hov... noget gik galt';
	modal.redirect = undefined;

	store.state.modal = modal;
};

export const loadProposal = async () => {
	if (location.search) {
		store.state.ProposalContext.quote_id = getQueryParameter('quote');
		store.state.ProposalContext.customerNo = getQueryParameter('customer_no');
		store.state.ProposalContext.signatureId = getQueryParameter('signature');
		store.state.ProposalContext.timestamp = getQueryParameter('timestamp') ?? '';
		if (getQueryParameter('environment')) {
			store.state.apiEnvironment = getQueryParameter('environment');
		}
	}

	const proposal = await getProposal();
	if (isDevelop && proposal?.status === 'TEST_DATA') {
		store.state.mockedDataLoaded = true;
		store.state.ProposalContext.quote_id = 'MOCK ' + proposal.data.quote_id;
		store.state.ProposalContext.customerNo = 'MOCK custNo';
		store.state.ProposalContext.signatureId = 'dummy signatureID';
		store.state.ProposalContext.timestamp = '01011970';
	}
	return proposal;
};

export const getElementWithReplacements = (elementToUpdate, replacements: Map<string, string>) => {
	try {
		if (!elementToUpdate) {
			return;
		}
		const elemString = JSON.stringify(elementToUpdate);
		const replacedString = getReplacedValue(elemString, replacements);
		return JSON.parse(replacedString);
	} catch (e) {
		Log.exception('Error replacing values', e, { quoteId: store.state.ProposalContext.quote_id });
		return elementToUpdate;
	}
};
